import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { worksheetBus } from "./worksheetBus";
import { toast } from "react-toastify";
import { RATE_LIMIT_FALLBACK_MESSAGE } from "../utils/rateLimit";

const WorksheetContext = createContext(null);

const resolveBases = () => {
  const raw = import.meta.env.VITE_APP_BASE_URL || "";
  const base = raw.replace(/\/+$/, "");

  const fetchBase = base
    ? `${base}/v2/worksheetEvaluation`
    : "/v2/worksheetEvaluation";
  const submitEndpoint = base
    ? `${base}/v2/worksheetEvaluation/submit`
    : "/v2/worksheetEvaluation/submit";
  const streamBase = base
    ? `${base}/v2/worksheetEvaluation/stream`
    : "/v2/worksheetEvaluation/stream";

  return { fetchBase, submitEndpoint, streamBase };
};

export const WorksheetProvider = ({ children }) => {
  const { fetchBase, submitEndpoint, streamBase } = useMemo(resolveBases, []);
  const token =
    typeof window !== "undefined"
      ? window.localStorage?.getItem("authToken") || ""
      : "";
  const toastRegistryRef = useRef(new Map());

  const value = useMemo(
    () => ({
      submitWithFormData: async ({ formData }) => {
        try {
          return await worksheetBus.submit({
            endpoint: submitEndpoint,
            formData,
            token,
          });
        } catch (error) {
          const rawMessage =
            typeof error?.message === "string" ? error.message : "";
          const normalized = rawMessage.toLowerCase();
          if (
            normalized.includes("rate limit") ||
            normalized.includes("rate_limit") ||
            normalized.includes("rate-limit") ||
            normalized.includes("429")
          ) {
            throw new Error(rawMessage || RATE_LIMIT_FALLBACK_MESSAGE);
          }
          throw error;
        }
      },
      attachById: (requestId) =>
        worksheetBus.attach({
          streamBase,
          requestId,
          token,
        }),
      status: (requestId) => worksheetBus.status(fetchBase, requestId),
      history: (limit = 10, offset = 0) =>
        worksheetBus.history(fetchBase, token, limit, offset),
      subscribe: (listener) => worksheetBus.subscribe(listener),
      cancel: (requestId) => worksheetBus.cancel(requestId),
    }),
    [fetchBase, streamBase, submitEndpoint, token]
  );

  useEffect(() => {
    const toastRegistry = toastRegistryRef.current;

    const unsubscribe = worksheetBus.subscribe((event) => {
      if (!event || !event.requestId) {
        return;
      }

      const toastId = `worksheet:${event.requestId}`;
      const percentText =
        typeof event.percent === "number"
          ? ` (${Math.round(event.percent)}%)`
          : "";

      if (event.type === "status_update") {
        if (event.status === "completed") {
          // Allow the completion event to handle final messaging.
          return;
        }

        // if (!toastRegistry.has(event.requestId)) {
        //   toastRegistry.set(
        //     event.requestId,
        //     toast.info(
        //       `Worksheet evaluation ${
        //         event.status || "in progress"
        //       }${percentText}`,
        //       {
        //         toastId,
        //         autoClose: false,
        //         closeOnClick: false,
        //         draggable: false,
        //       }
        //     )
        //   );
        // }
        return;
      }

      if (event.type === "evaluation_complete") {
        console.log("Evaluation complete event:", event);
        const message =
          event.message ||
          `Worksheet evaluation completed (${event.requestId})`;
        toast.success(message, { toastId });
        toastRegistry.delete(event.requestId);
        return;
      }

      if (event.type === "evaluation_error") {
        const message =
          event.error || `Worksheet evaluation failed (${event.requestId})`;
        if (toastRegistry.has(event.requestId) || toast.isActive(toastId)) {
          toast.update(toastId, {
            render: message,
            type: toast.TYPE.ERROR,
            autoClose: 6000,
            closeOnClick: true,
            draggable: true,
            isLoading: false,
          });
        } else {
          toast.error(message, { toastId });
        }
        toastRegistry.delete(event.requestId);
      }
    });

    return () => {
      unsubscribe();
      for (const [, activeToastId] of toastRegistry.entries()) {
        toast.dismiss(activeToastId);
      }
      toastRegistry.clear();
    };
  }, []);

  return (
    <WorksheetContext.Provider value={value}>
      {children}
    </WorksheetContext.Provider>
  );
};

export const useWorksheet = () => {
  const ctx = useContext(WorksheetContext);
  if (!ctx) {
    throw new Error("useWorksheet must be used within a WorksheetProvider");
  }
  return ctx;
};
